'use client'

import {
  Children,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'

import { cn } from '@/lib/utils'

/* A terminal that reads as a real one rather than a code block with three dots
   on it: titled window chrome, authentic traffic lights, and a caret that
   actually sits at the end of the line being typed.

   Children run as a sequence — each line starts when the one before it
   finishes — so callers write the transcript in order and never hand-tune
   delays. Reduced motion renders the finished transcript with no typing. */

/* ─── Sequencing ─────────────────────────────────────────────────────── */

interface SequenceValue {
  completeItem: (index: number) => void
  activeIndex: number
  started: boolean
  /** Reduced motion: paint every line at once, no typing, no caret. */
  instant: boolean
}

const SequenceContext = createContext<SequenceValue | null>(null)
const ItemIndexContext = createContext<number | null>(null)

const useSequence = () => useContext(SequenceContext)
const useItemIndex = () => useContext(ItemIndexContext)

/** True once this child's turn has come up (or immediately, unsequenced). */
function useTurn(startOnView: boolean, inView: boolean) {
  const sequence = useSequence()
  const index = useItemIndex()
  const [reached, setReached] = useState(false)

  useEffect(() => {
    if (!sequence || index === null) return
    if (!sequence.started || reached) return
    if (sequence.activeIndex >= index) setReached(true)
  }, [sequence, index, reached])

  if (!sequence) return startOnView ? inView : true
  return reached
}

/* ─── Lines ──────────────────────────────────────────────────────────── */

export function AnimatedSpan({
  children,
  className,
  startOnView = false,
  delay = 0,
}: {
  children: ReactNode
  className?: string
  startOnView?: boolean
  delay?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { amount: 0.3, once: true })
  const sequence = useSequence()
  const index = useItemIndex()
  const show = useTurn(startOnView, inView)
  const instant = sequence?.instant ?? false

  // Output lines have nothing to type. They hand the turn on after a beat so a
  // block of stdout cascades instead of appearing in one frame.
  const completeRef = useRef<SequenceValue['completeItem'] | null>(null)
  const indexRef = useRef<number | null>(null)
  useEffect(() => {
    completeRef.current = sequence?.completeItem ?? null
    indexRef.current = index
  })

  useEffect(() => {
    if (!show) return
    const hand = () => {
      if (completeRef.current && indexRef.current !== null) {
        completeRef.current(indexRef.current)
      }
    }
    if (instant) {
      hand()
      return
    }
    const id = setTimeout(hand, 90)
    return () => clearTimeout(id)
  }, [show, instant])

  return (
    <motion.div
      ref={ref}
      initial={instant ? false : { opacity: 0, y: -4 }}
      animate={show ? { opacity: 1, y: 0 } : { opacity: 0, y: -4 }}
      transition={{ duration: 0.22, delay: sequence ? 0 : delay / 1000 }}
      className={cn('grid whitespace-pre-wrap', className)}
    >
      {children}
    </motion.div>
  )
}

export function TypingAnimation({
  children,
  className,
  duration = 38,
  delay = 0,
  startOnView = true,
}: {
  children: string
  className?: string
  /** ms per character */
  duration?: number
  delay?: number
  startOnView?: boolean
}) {
  if (typeof children !== 'string') {
    throw new Error('TypingAnimation: children must be a string')
  }

  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { amount: 0.3, once: true })
  const sequence = useSequence()
  const index = useItemIndex()
  const instant = sequence?.instant ?? false
  const turn = useTurn(startOnView, inView)

  const [typed, setTyped] = useState('')
  const [done, setDone] = useState(false)

  // completeItem identity changes every tick, so hold it in a ref and keep it
  // out of the typing effect's deps — otherwise the interval restarts on each
  // character and the line never finishes.
  const completeRef = useRef<SequenceValue['completeItem'] | null>(null)
  const indexRef = useRef<number | null>(null)
  useEffect(() => {
    completeRef.current = sequence?.completeItem ?? null
    indexRef.current = index
  })

  const started = turn && !instant
  useEffect(() => {
    if (instant) return
    if (!started) return

    let i = 0
    const id = setInterval(() => {
      i += 1
      setTyped(children.slice(0, i))
      if (i >= children.length) {
        clearInterval(id)
        setDone(true)
        if (completeRef.current && indexRef.current !== null) {
          completeRef.current(indexRef.current)
        }
      }
    }, duration)

    return () => clearInterval(id)
  }, [started, children, duration, instant])

  // Reduced motion: full line, and release the sequence immediately.
  useEffect(() => {
    if (!instant || !turn) return
    if (completeRef.current && indexRef.current !== null) {
      completeRef.current(indexRef.current)
    }
  }, [instant, turn])

  const text = instant ? children : typed
  const caret = !instant && started && !done

  return (
    <span ref={ref} className={cn('whitespace-pre-wrap', className)}>
      {text}
      {caret && (
        <span
          aria-hidden
          className="terminal-caret ml-px inline-block h-[1.05em] w-[0.5em] translate-y-[0.15em] bg-current align-baseline"
        />
      )}
    </span>
  )
}

/* ─── Window ─────────────────────────────────────────────────────────── */

/** Real macOS button colours; the ring keeps them from floating on the bar. */
const LIGHTS = ['#ff5f57', '#febc2e', '#28c840'] as const

export function Terminal({
  children,
  title = 'zsh',
  className,
  bodyClassName,
  startOnView = true,
}: {
  children: ReactNode
  /** Shown centred in the title bar, the way a real window titles itself. */
  title?: string
  className?: string
  bodyClassName?: string
  startOnView?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { amount: 0.25, once: true })
  const reduced = useReducedMotion()

  const [activeIndex, setActiveIndex] = useState(0)
  const started = !startOnView || inView

  const value = useMemo<SequenceValue>(
    () => ({
      completeItem: (index) =>
        setActiveIndex((current) => (index >= current ? index + 1 : current)),
      activeIndex,
      started,
      instant: !!reduced,
    }),
    [activeIndex, started, reduced]
  )

  const wrapped = useMemo(
    () =>
      Children.toArray(children).map((child, index) => (
        <ItemIndexContext.Provider key={index} value={index}>
          {child}
        </ItemIndexContext.Provider>
      )),
    [children]
  )

  return (
    <SequenceContext.Provider value={value}>
      <div
        ref={ref}
        className={cn(
          'w-full overflow-hidden rounded-xl',
          // Follows the theme: a real terminal is light on a light desktop.
          'bg-white ring-1 ring-black/10',
          'dark:bg-zinc-950 dark:ring-white/10',
          'shadow-[0_1px_1px_rgba(0,0,0,0.06),0_8px_24px_-8px_rgba(0,0,0,0.14),0_24px_56px_-24px_rgba(0,0,0,0.18)]',
          'dark:shadow-[0_1px_1px_rgba(0,0,0,0.14),0_8px_24px_-8px_rgba(0,0,0,0.4),0_24px_56px_-24px_rgba(0,0,0,0.5)]',
          className
        )}
      >
        {/* Title bar: lights left, title optically centred over the full bar. */}
        <div
          className={cn(
            'relative flex h-9 shrink-0 items-center px-3.5',
            'border-b border-black/[0.08] bg-gradient-to-b from-zinc-100 to-zinc-200',
            'dark:border-white/[0.07] dark:from-zinc-800 dark:to-zinc-900'
          )}
        >
          <div className="flex items-center gap-2">
            {LIGHTS.map((c) => (
              <span
                key={c}
                aria-hidden
                className="size-[11px] rounded-full shadow-[inset_0_0_0_0.5px_rgba(0,0,0,0.25)]"
                style={{ backgroundColor: c }}
              />
            ))}
          </div>

          <span className="pointer-events-none absolute inset-x-0 text-center text-[11px] font-medium text-black/45 dark:text-white/40">
            {title}
          </span>
        </div>

        <pre
          className={cn(
            'overflow-auto p-4 font-mono text-[12.5px] leading-[1.7] sm:text-[13px]',
            'text-zinc-600 dark:text-zinc-300',
            bodyClassName
          )}
        >
          <code className="grid gap-y-0.5">{wrapped}</code>
        </pre>
      </div>

      <style jsx global>{`
        .terminal-caret {
          animation: terminal-caret 1s steps(1) infinite;
        }
        @keyframes terminal-caret {
          0%,
          50% {
            opacity: 1;
          }
          50.01%,
          100% {
            opacity: 0;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .terminal-caret {
            animation: none;
          }
        }
      `}</style>
    </SequenceContext.Provider>
  )
}

/* ─── Transcript helpers ─────────────────────────────────────────────── */

/* Ink tokens. Every colour in a transcript comes from here so call sites never
   hardcode a shade that only works in one theme. */
export const TERM = {
  /** The typed command — the highest-contrast thing in the window. */
  cmd: 'text-zinc-900 dark:text-zinc-100',
  /** Emphasised value inside an output line. */
  key: 'text-zinc-700 dark:text-zinc-300',
  /** Leaders, padding, punctuation. */
  dim: 'text-zinc-400 dark:text-zinc-500',
  /** Success summary line. */
  ok: 'text-emerald-600 dark:text-emerald-400',
} as const

/** `user:dir $` — the shell prompt, dimmed so the command reads first. */
export function Prompt({ dir = '~' }: { dir?: string }) {
  return (
    <span aria-hidden className="select-none">
      <span className="text-emerald-600 dark:text-emerald-400">euclideum</span>
      <span className={TERM.dim}>:</span>
      <span className="text-sky-600 dark:text-sky-400">{dir}</span>
      <span className={TERM.dim}> $ </span>
    </span>
  )
}

/** Output line, optionally prefixed with a status glyph. */
export function Line({
  tone = 'muted',
  glyph,
  children,
}: {
  tone?: 'ok' | 'warn' | 'info' | 'muted'
  glyph?: string
  children: ReactNode
}) {
  const color = {
    ok: 'text-emerald-600 dark:text-emerald-400',
    warn: 'text-amber-600 dark:text-amber-400',
    info: 'text-sky-600 dark:text-sky-400',
    muted: 'text-zinc-400 dark:text-zinc-500',
  }[tone]

  return (
    <span className="text-zinc-600 dark:text-zinc-400">
      {glyph && <span className={cn('select-none', color)}>{glyph} </span>}
      {children}
    </span>
  )
}
